import { Add, Send } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { ROUTE_PATHS } from '../../../routes/Routes';
import { listPageTableRowStyling } from '../../../styles/sxStyling';
import { FormTemplateLink } from '../../../types/FormTemplate';
import { UserSettingsCollapseKeys } from '../../../types/User';
import ResponsiveButton from '../../../uikit/buttons/ResponsiveButton';
import { addOrRemoveDaysToDate } from '../../../utility/dateUtils';
import AccordionSection from '../../AccordionSection';
import RolesPopover from '../popover/RolesPopover';
import { TemplateNameContent, TemplateSectionModule } from '../Template/TemplateNameContent';
import AddFormTemplateModal from './AddFormTemplateModal';
import FormSubmissionsModal from './FormSubmissionsModal';
import InviteSendModal from './InviteSendModal';

interface IFormTemplatesSectionProps {
  moduleType: TemplateSectionModule;
  linkingModels: FormTemplateLink[];
  setLinkingModels: (linkingModels: FormTemplateLink[]) => void;
  collapseId: UserSettingsCollapseKeys;
  accountUuid?: string;
  startDate?: string;
}

type ModalOpen = 'modify' | 'invite' | 'view' | null;

export default function FormTemplatesSection({ moduleType, linkingModels, setLinkingModels, collapseId, accountUuid, startDate }: IFormTemplatesSectionProps) {
  const { disabled } = useContext(DisableContext);

  const [modalOpen, setModalOpen] = useState<ModalOpen>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplateLink | null>(null);
  const isEvent = moduleType === TemplateSectionModule.EVENT;

  const handleAddFormTemplate = (linkingData: FormTemplateLink) => {
    if (selectedTemplate) {
      setLinkingModels(
        _.map(linkingModels, (template) => (template.uuid === selectedTemplate.uuid ? { ...linkingData, new_record: template.new_record } : template))
      );
    } else {
      setLinkingModels([...linkingModels, { ...linkingData, new_record: true }]);
    }

    closeModal();
  };

  const handleRemoveFormTemplate = (uuid: string) => {
    setLinkingModels(_.reject(linkingModels, { uuid }));
  };

  const closeModal = () => {
    setSelectedTemplate(null);
    setModalOpen(null);
  };

  const openModal = (template: FormTemplateLink | null, newModalOpen: NonNullable<ModalOpen>) => {
    setSelectedTemplate(template);
    setModalOpen(newModalOpen);
  };

  const formTemplateTableHeaderActions = (
    <ResponsiveButton startIcon={<Add />} variant="outlined" disabled={disabled} onClick={() => openModal(null, 'modify')}>
      Add
    </ResponsiveButton>
  );

  const formTemplateTableRows = _.map(linkingModels, (linkingModel: FormTemplateLink) => {
    return (
      <TableRow key={linkingModel.uuid} sx={{ ...listPageTableRowStyling }}>
        <TableCell>
          <TemplateNameContent
            moduleType={moduleType}
            isNew={linkingModel.new_record}
            name={linkingModel.form_template_name}
            url={`/${ROUTE_PATHS.FORM_TEMPLATES}/${linkingModel.form_template_uuid}`}
            onClick={() => openModal(linkingModel, 'view')}
          />
        </TableCell>
        <TableCell>{addOrRemoveDaysToDate({ date: startDate, days: linkingModel.open_offset ?? 0 })}</TableCell>
        <TableCell>{addOrRemoveDaysToDate({ date: startDate, days: linkingModel.close_offset ?? 0 })}</TableCell>
        <TableCell>
          <RolesPopover roles={linkingModel.roles_values} />
        </TableCell>
        <TableCell sx={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEvent && !linkingModel.new_record && (
            <>
              <IconButton onClick={() => openModal(linkingModel, 'invite')}>
                <Send />
              </IconButton>
            </>
          )}
          {!disabled && (
            <>
              <IconButton onClick={() => openModal(linkingModel, 'modify')}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleRemoveFormTemplate(linkingModel.uuid)}>
                <DeleteIcon />
              </IconButton>
            </>
          )}
        </TableCell>
      </TableRow>
    );
  });

  const FORM_TEMPLATE_LABELS = {
    [TemplateSectionModule.EVENT]: {
      formTitle: 'Forms',
      formNameTitle: 'Form Name',
      openOffsetTitle: 'Send Date',
      closeOffsetTitle: 'Due Date',
    },
    [TemplateSectionModule.EVENT_TEMPLATE]: {
      formTitle: 'Form Templates',
      formNameTitle: 'Form Template Name',
      openOffsetTitle: 'Open Offset',
      closeOffsetTitle: 'Close Offset',
    },
  };
  const { formTitle, formNameTitle, openOffsetTitle, closeOffsetTitle } = FORM_TEMPLATE_LABELS[moduleType];

  return (
    <>
      <AccordionSection title={formTitle} headerElements={formTemplateTableHeaderActions} collapseId={collapseId} hasData={linkingModels.length > 0} scrollable>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{formNameTitle}</TableCell>
                <TableCell>{openOffsetTitle}</TableCell>
                <TableCell>{closeOffsetTitle}</TableCell>
                <TableCell>Recipient Roles</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{formTemplateTableRows}</TableBody>
          </Table>
        </TableContainer>
      </AccordionSection>

      <AddFormTemplateModal isOpen={modalOpen == 'modify'} onCancel={closeModal} onAdd={handleAddFormTemplate} formTemplateLink={selectedTemplate} />

      <FormSubmissionsModal isOpen={modalOpen == 'view'} onClose={closeModal} formTemplateLink={selectedTemplate} />

      <InviteSendModal isOpen={modalOpen === 'invite'} onCancel={closeModal} formTemplateLink={selectedTemplate} accountUuid={accountUuid} />
    </>
  );
}
